import React, { useEffect, useState, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay, EffectFade, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, ArrowRight } from 'lucide-react';
import { normalizeImageUrl } from '../utils/imageUtils';
import { useData } from '../context/DataContext';

const eventSlides = [
    { img: '/assets/ceo-nguyen-ngoc-ha-du-ky-niem-20-nam-thanh-lap-truong-2-1.jpg', alt: 'CEO Nguyễn Ngọc Hà', href: '#' },
    { img: '/assets/ha-thanh-garage-ky-ket-hop-tac-voi-expel-4.jpg', alt: 'Ký kết hợp tác XPEL', href: '#' },
    { img: '/assets/khai-truong-ha-thanh-garage-chi-nhanh-thach-ban-long-bien-1-1.jpg', alt: 'Khai trương chi nhánh Thạch Bàn', href: '#' },
];

// Reviews array removed as requested to use backend data only.

const HomePage = () => {
    const { banners, homeData, typicalCustomers, aboutUs, settings, loading } = useData();

    useEffect(() => {
        AOS.init({
            duration: 800,
            once: true,
            easing: 'ease-out-cubic',
            offset: 50,
        });
    }, []);

    const bannerSlides = useMemo(() => {
        if (!banners || !Array.isArray(banners)) return [];
        return [...banners]
            .sort((a, b) => (a.order ?? a.sortOrder ?? 10) - (b.order ?? b.sortOrder ?? 10))
            .map(bn => ({
                img: normalizeImageUrl(bn.imageUrl || bn.urlImg),
                alt: bn.title || 'Banner',
                href: bn.link || '#',
            }));
    }, [banners]);

    const aboutUsSlides = useMemo(() => {
        const hero = aboutUs?.introHeroImage;
        const legacy = aboutUs?.imageUrls;
        const urls = hero
            ? [hero, ...(Array.isArray(legacy) ? legacy.filter((u) => u && u !== hero) : [])]
            : (Array.isArray(legacy) ? legacy : []);

        if (urls.length === 0) return eventSlides;

        return urls.map((url, i) => ({
            img: normalizeImageUrl(url),
            alt: aboutUs?.introBrandTitle || aboutUs?.title || `About Us ${i + 1}`,
            href: '#'
        }));
    }, [aboutUs]);

    const homeServices = homeData?.services || [];
    const homeNews = homeData?.news || [];
    const customers = typicalCustomers;

    const [mobileSwiperRef, setMobileSwiperRef] = useState(null);
    const [desktopSwiperRef, setDesktopSwiperRef] = useState(null);
    const [mobileSlide, setMobileSlide] = useState(0);

    // Mobile auto-play
    useEffect(() => {
        if (!bannerSlides.length) return;
        const t = setInterval(() => setMobileSlide(i => (i + 1) % bannerSlides.length), 5000);
        return () => clearInterval(t);
    }, [bannerSlides.length]);

    return (
        <main>
            {/* ════ BANNER SLIDE — full width, no container ════ */}
            <section className="index-slide relative group">
                <div className="main-slide relative">
                    {/* ── MOBILE BANNER: carousel thuần React, không Swiper ── */}
                    <div className="block lg:hidden w-full">
                        {/* Carousel ảnh */}
                        <div style={{ position: 'relative', width: '100%', overflow: 'hidden', lineHeight: 0 }}>
                            {loading && bannerSlides.length === 0 ? (
                                <div className="w-full relative bg-slate-100 flex flex-col items-center justify-center overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                                        <div className="relative flex items-center justify-center w-12 h-12 mb-3">
                                            <div className="absolute inset-0 rounded-full border-[3px] border-slate-200" />
                                            <div className="absolute inset-0 rounded-full border-[3px] border-red-600 border-t-transparent animate-spin" />
                                        </div>
                                        <div className="text-[10px] font-bold tracking-widest text-slate-800 uppercase flex items-center">
                                            Đang tải
                                            <span className="flex gap-[2px] ml-1">
                                                <span className="w-1 h-1 rounded-full bg-red-600 animate-bounce" style={{ animationDelay: "0ms" }} />
                                                <span className="w-1 h-1 rounded-full bg-red-600 animate-bounce" style={{ animationDelay: "150ms" }} />
                                                <span className="w-1 h-1 rounded-full bg-red-600 animate-bounce" style={{ animationDelay: "300ms" }} />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {bannerSlides.map((slide, i) => (
                                        <div key={i} style={{ display: i === mobileSlide ? 'block' : 'none', lineHeight: 0 }}>
                                            <a href={slide.href} title={slide.alt} style={{ display: 'block', lineHeight: 0 }}>
                                                <img
                                                    src={slide.img}
                                                    alt={slide.alt}
                                                    style={{ display: 'block', width: '100%', height: 'auto', verticalAlign: 'top' }}
                                                    fetchPriority={i === 0 ? "high" : "auto"}
                                                    loading={i === 0 ? "eager" : "lazy"}
                                                />
                                            </a>
                                        </div>
                                    ))}
                                    {/* Nút Prev */}
                                    {bannerSlides.length > 1 && <button
                                        onClick={() => setMobileSlide(i => (i - 1 + bannerSlides.length) % bannerSlides.length)}
                                        style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', zIndex: 10, width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: 'rgba(0,0,0,0.4)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer' }}>
                                        <ChevronLeft size={22} />
                                    </button>}
                                    {/* Nút Next */}
                                    {bannerSlides.length > 1 && <button
                                        onClick={() => setMobileSlide(i => (i + 1) % bannerSlides.length)}
                                        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', zIndex: 10, width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: 'rgba(0,0,0,0.4)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer' }}>
                                        <ChevronRight size={22} />
                                    </button>}
                                </>
                            )}
                        </div>

                        {/* Dải thông tin — liền sát ngay dưới ảnh */}
                        <div style={{ background: 'linear-gradient(135deg, #c0392b 0%, #e74c3c 50%, #c0392b 100%)', padding: '10px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.17 3.39 2 2 0 0 1 3.16 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16z" /></svg>
                                    </div>
                                    <div>
                                        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '9px', margin: 0 }}>HOTLINE</p>
                                        <a href={`tel:${settings?.hotline}`} style={{ color: '#fff', fontWeight: 700, fontSize: '14px', textDecoration: 'none' }}>{settings?.hotline}</a>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                    </div>
                                    <div>
                                        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '9px', margin: 0 }}>GIỜ MỞ CỬA</p>
                                        <p style={{ color: '#fff', fontWeight: 700, fontSize: '13px', margin: 0 }}>8:00 – 17:30</p>
                                    </div>
                                </div>
                            </div>
                            <div style={{ height: '1px', background: 'rgba(255,255,255,0.25)', margin: '6px 0' }} />
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>
                                </div>
                                <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '11px', margin: 0, lineHeight: 1.5 }}>
                                    50 Đường Bưởi, Phường Giảng Võ, TP Hà Nội                                </p>
                            </div>
                        </div>
                        {/* Chấm pagination */}
                        {!loading && bannerSlides.length > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', padding: '8px 0', background: '#fff' }}>
                                {bannerSlides.map((_, i) => (
                                    <button key={i} onClick={() => setMobileSlide(i)}
                                        style={{ width: i === mobileSlide ? '28px' : '10px', height: '10px', borderRadius: i === mobileSlide ? '5px' : '50%', background: i === mobileSlide ? '#e74c3c' : 'rgba(0,0,0,0.2)', border: 'none', cursor: 'pointer', padding: 0, transition: 'all 0.3s' }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* ── DESKTOP BANNER: full viewport height, object-cover ── */}
                    <div className="hidden lg:block w-full relative" style={{ height: 'calc(100vh - 85px)' }}>
                        {loading && bannerSlides.length === 0 ? (
                            <div className="w-full h-full bg-slate-100 flex flex-col items-center justify-center relative shadow-inner">
                                {/* Decorative tech background */}
                                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,0,0,0.03)_0%,transparent_70%)]" />

                                <div className="relative flex items-center justify-center w-24 h-24 mb-6 z-10">
                                    <div className="absolute inset-0 rounded-full border-[4px] border-slate-200" />
                                    <div className="absolute inset-0 rounded-full border-[4px] border-red-600 border-t-transparent animate-spin" />
                                    <div className="absolute inset-2 rounded-full border-[4px] border-slate-200 border-b-transparent animate-[spin_1.5s_reverse_linear_infinite]" />
                                    <div className="absolute inset-4 rounded-full bg-slate-200 animate-pulse" />
                                </div>
                                <div className="z-10 flex flex-col items-center">
                                    <div className="text-xl font-bold tracking-widest text-slate-800 uppercase flex items-center">
                                        Đang tải dữ liệu
                                        <span className="flex gap-1 ml-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-bounce" style={{ animationDelay: "0ms" }} />
                                            <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-bounce" style={{ animationDelay: "150ms" }} />
                                            <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-bounce" style={{ animationDelay: "300ms" }} />
                                        </span>
                                    </div>
                                    <p className="text-slate-400 text-sm mt-3 font-medium tracking-wide">Vui lòng chờ trong giây lát...</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <Swiper
                                    key={`d-${bannerSlides.length}`}
                                    onSwiper={setDesktopSwiperRef}
                                    modules={[Autoplay, Pagination]}
                                    pagination={{ clickable: true, el: '.desktop-banner-pagination' }}
                                    autoplay={{ delay: 5000, disableOnInteraction: false }}
                                    loop={bannerSlides.length > 1}
                                    observer={true}
                                    observeParents={true}
                                    speed={1000}
                                    className="w-full h-full"
                                >
                                    {bannerSlides.map((slide, i) => (
                                        <SwiperSlide key={i}>
                                            <a href={slide.href} title={slide.alt} className="block w-full h-full overflow-hidden group">
                                                <img
                                                    src={slide.img}
                                                    alt={slide.alt}
                                                    loading={i === 0 ? "eager" : "lazy"}
                                                    fetchPriority={i === 0 ? "high" : "auto"}
                                                    className="w-full h-full object-cover object-center transition-transform duration-[3000ms] group-hover:scale-105"
                                                />
                                            </a>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                                {/* Nút điều hướng Desktop */}
                                {bannerSlides.length > 1 && (
                                    <>
                                        <button
                                            onClick={() => desktopSwiperRef?.slidePrev()}
                                            className="banner-prev absolute left-6 top-1/2 -translate-y-1/2 z-[110] w-14 h-14 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/70 transition-all duration-300 backdrop-blur-md border border-white/20 shadow-2xl cursor-pointer"
                                        >
                                            <ChevronLeft size={32} />
                                        </button>
                                        <button
                                            onClick={() => desktopSwiperRef?.slideNext()}
                                            className="banner-next absolute right-6 top-1/2 -translate-y-1/2 z-[110] w-14 h-14 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/70 transition-all duration-300 backdrop-blur-md border border-white/20 shadow-2xl cursor-pointer"
                                        >
                                            <ChevronRight size={32} />
                                        </button>
                                    </>
                                )}
                                {/* Pagination Desktop */}
                                <div className="desktop-banner-pagination absolute bottom-8 left-0 right-0 z-[100] flex justify-center items-center gap-3"></div>
                            </>
                        )}
                    </div>

                </div>

                <style>{`
                    .mySwiper img {
                        display: block;
                    }
                    .main-slide .swiper-button-next, 
                    .main-slide .swiper-button-prev {
                        display: none !important;
                    }
                    .banner-pagination .swiper-pagination-bullet {
                        width: 12px;
                        height: 12px;
                        background: rgba(255, 255, 255, 0.4);
                        opacity: 1;
                        border: 1px solid rgba(0, 0, 0, 0.2);
                        transition: all 0.3s ease;
                    }
                    .banner-pagination .swiper-pagination-bullet-active {
                        width: 32px;
                        border-radius: 6px;
                        background: #f97316;
                    }
                    .welcome-text {
                        text-align: left;
                        margin-left: 0;
                        margin-right: auto;
                    }
                    @media (max-width: 767px) {
                        .welcome-text {
                            text-align: center !important;
                            margin-left: auto !important;
                            margin-right: auto !important;
                        }
                    }
                `}</style>
            </section>

            {/* ════ WELCOME ════ */}
            <section className="py-12 lg:py-24 bg-white overflow-hidden">
                <div className="container max-w-7xl mx-auto px-4">
                    <div className="flex flex-col md:flex-row items-center gap-10 lg:gap-16">
                        <div className="w-full md:w-[48%]" data-aos="fade-right">
                            <div className="relative">
                                <span className="text-red-600 font-bold uppercase tracking-[0.4em] text-[10px] mb-5 block welcome-text">
                                    Welcome to Trung Anh Garage
                                </span>
                                <h2 className="text-3xl lg:text-[38px] font-black text-slate-900 uppercase leading-tight mb-8 welcome-text max-w-none text-center lg:text-left">
                                    Trung tâm <br className="md:hidden" /> <span style={{ color: '#e74c3c' }} className="block md:inline text-2xl md:text-3xl lg:text-[38px]">Sửa chữa xe chuyên nghiệp</span>
                                </h2>
                                <div className="text-[14.5px] text-slate-500 leading-relaxed mb-10 welcome-text py-2 font-medium max-w-none whitespace-pre-line">
                                    {loading ? (
                                        <div className="space-y-3">
                                            <div className="h-4 bg-slate-200 rounded w-full animate-pulse" />
                                            <div className="h-4 bg-slate-200 rounded w-5/6 animate-pulse" />
                                            <div className="h-4 bg-slate-200 rounded w-4/6 animate-pulse" />
                                            <div className="h-4 bg-slate-200 rounded w-[90%] animate-pulse" />
                                        </div>
                                    ) : (
                                        (Array.isArray(aboutUs?.introParagraphs) && aboutUs.introParagraphs.length
                                            ? aboutUs.introParagraphs.join('\n\n')
                                            : aboutUs?.description) || null
                                    )}
                                </div>
                                <div className="mb-10 welcome-text">
                                    {loading ? (
                                        <div className="flex gap-5 overflow-hidden pb-10">
                                            <div className="w-[60%] aspect-[4/3] rounded-[1.5rem] bg-slate-200 animate-pulse flex-shrink-0" />
                                            <div className="w-[60%] aspect-[4/3] rounded-[1.5rem] bg-slate-200 animate-pulse flex-shrink-0" />
                                        </div>
                                    ) : (
                                        <Swiper
                                            modules={[Autoplay, Pagination]}
                                            spaceBetween={20}
                                            slidesPerView={1.5}
                                            breakpoints={{
                                                640: { slidesPerView: 2.5 },
                                                1024: { slidesPerView: 2.8 }
                                            }}
                                            autoplay={{ delay: 3500, disableOnInteraction: false }}
                                            loop
                                            pagination={{ clickable: true }}
                                            className="rounded-[2rem] overflow-hidden !pb-10"
                                        >
                                            {aboutUsSlides.map((ev, i) => (
                                                <SwiperSlide key={i}>
                                                    <a href={ev.href} className="group relative block aspect-[4/3] rounded-[1.5rem] overflow-hidden bg-slate-100 shadow-md hover:shadow-xl transition-all duration-500">
                                                        <img src={ev.img} alt={ev.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                                        <div className="absolute bottom-3 left-3 right-3 translate-y-3 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                                                            <div className="bg-white/10 backdrop-blur-md border border-white/20 p-2 rounded-xl text-[10px] text-white font-bold text-center uppercase tracking-widest text-shadow-sm">
                                                                Xem chi tiết
                                                            </div>
                                                        </div>
                                                    </a>
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>
                                    )}
                                </div>
                                <Link to="/dich-vu-nhom/bao-duong-sua-chua-dong-co-o-to" title="Khám phá dịch vụ" className="inline-flex items-center gap-3 bg-primary hover:bg-red-500 text-white hover:text-white font-black py-4 px-10 rounded-full transition-all duration-300 transform hover:-translate-y-1 shadow-[0_15px_40px_rgba(255,0,0,0.2)] uppercase tracking-widest text-[11px]">
                                    Khám phá ngay <ArrowRight size={18} />
                                </Link>
                            </div>
                        </div>
                        <div className="w-full md:w-[52%]" data-aos="fade-left">
                            <div className="relative aspect-video rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.15)] border-[10px] border-white group animate-float bg-slate-100">
                                <iframe
                                    className="w-full h-full"
                                    title="YouTube video player"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                    src="https://www.youtube.com/embed/30vmTDK-fcA"
                                ></iframe>
                                <div className="absolute inset-0 pointer-events-none border border-black/5 rounded-[1.5rem] shadow-inner" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ════ SERVICES — LUXURY REDESIGN ════ */}
            <section className="relative py-12 lg:py-24 bg-mesh-light overflow-hidden">
                {/* Abstract background elements */}
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-600/5 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-red-600/[0.03] blur-[80px] rounded-full translate-y-1/2 -translate-x-1/2" />

                <div className="container max-w-7xl mx-auto px-4 relative z-10">
                    <div className="flex flex-col items-center mb-20" data-aos="fade-down">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-12 h-[1px] bg-slate-200" />
                            <span className="text-red-600 font-bold uppercase tracking-[0.5em] text-[10px]">Quality Excellence</span>
                            <div className="w-12 h-[1px] bg-slate-200" />
                        </div>
                        <h3 className="text-4xl md:text-6xl font-black text-slate-900 uppercase tracking-tighter text-center leading-none">
                            Dịch vụ <span className="text-red-600">đẳng cấp</span>
                        </h3>
                        <p className="mt-6 text-slate-400 text-sm font-medium uppercase tracking-[0.2em] text-center max-w-lg">
                            Giải pháp sửa chữa toàn diện cho xế yêu của bạn
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
                        {loading ? (
                            Array(8).fill(0).map((_, i) => (
                                <div key={i} className="bg-slate-200/50 rounded-[2.8rem] aspect-[4/5] animate-pulse shadow-md" />
                            ))
                        ) : homeServices.map((sv, i) => (
                            <div
                                key={sv.id || i}
                                className="group relative glass-premium rounded-[2.8rem] p-5 card-glow-red transition-all duration-500 hover:-translate-y-4 overflow-hidden flex flex-col"
                                data-aos="fade-up"
                                data-aos-delay={i * 100}
                            >
                                {/* Decorative Numbering */}
                                <div className="absolute -top-4 -right-1 text-[8rem] font-black service-card-number opacity-20 pointer-events-none group-hover:opacity-40 transition-opacity duration-700">
                                    {i + 1 < 10 ? `0${i + 1}` : i + 1}
                                </div>

                                {/* Image Preview with Zoom */}
                                <div className="relative aspect-[4/5] rounded-[2.2rem] overflow-hidden mb-8 z-10 shadow-2xl group/img">
                                    <img
                                        src={normalizeImageUrl(sv.imageUrl)}
                                        alt={sv.title}
                                        className="w-full h-full object-cover transition-transform duration-1000 group-hover/img:scale-110"
                                        loading="lazy"
                                    />
                                    {/* Glass Overlay on Hover */}
                                    <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] opacity-0 group-hover/img:opacity-100 transition-all duration-500 flex flex-col items-center justify-end p-6">
                                        <Link
                                            to={`/dich-vu/${sv.slug}`}
                                            className="w-full bg-white text-slate-900 rounded-2xl py-4 text-[11px] font-black uppercase tracking-[0.2em] shadow-xl transform translate-y-4 group-hover/img:translate-y-0 transition-transform duration-500 text-center hover:bg-primary hover:text-white"
                                        >
                                            Chi tiết dịch vụ
                                        </Link>
                                    </div>
                                </div>

                                <div className="relative z-10 flex flex-col flex-grow px-2">
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="w-8 h-[2px] bg-red-600 rounded-full" />
                                        <span className="text-red-600 font-black uppercase tracking-[0.3em] text-[9px]">Premium Care</span>
                                    </div>

                                    <h4 className="text-slate-900 font-extrabold text-[17px] uppercase leading-tight mb-5 group-hover:text-red-700 transition-colors line-clamp-2">
                                        {sv.title}
                                    </h4>

                                    <div className="mt-auto flex items-center justify-between border-t border-slate-100 italic">
                                        <Link to="/dat-lich-hen" className="inline-flex items-center gap-2 text-red-600 font-black text-[12px] uppercase tracking-widest group/btn">
                                            <span>Đặt lịch</span>
                                            <div className="w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center transition-transform group-hover/btn:translate-x-2">
                                                <Calendar size={14} />
                                            </div>
                                        </Link>
                                        <div className="flex gap-1">
                                            {[1, 2, 3].map(dot => (
                                                <div key={dot} className="w-1 h-1 bg-red-600/30 rounded-full" />
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Hover Glow */}
                                <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-red-600/5 blur-[60px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ════ VỀ CHÚNG TÔI ════ */}
            <section className="py-16 lg:py-32 bg-slate-50 relative overflow-hidden">
                {/* Background Decoration */}
                <div className="absolute top-0 right-0 w-1/3 h-full bg-red-600/5 -skew-x-12 translate-x-1/2 pointer-events-none" />

                <div className="container max-w-7xl mx-auto px-4 relative z-10">
                    <div className="flex flex-col lg:flex-row items-center gap-20">
                        <div className="w-full lg:w-1/2" data-aos="fade-right">
                            <div className="relative">
                                <div className="absolute -top-10 -left-10 w-40 h-40 bg-red-600/10 rounded-full blur-3xl" />
                                <div className="relative rounded-[3rem] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.1)] border-8 border-white group">
                                    <img
                                        src="/assets/introduce.png"
                                        alt="Về Trung Anh Garage"
                                        className="w-full h-auto object-cover transition-transform duration-1000 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                </div>
                                {/* Experience Badge */}
                                <div className="absolute -bottom-6 -right-6 bg-red-600 text-white p-6 rounded-3xl shadow-2xl z-20 hidden md:block" data-aos="zoom-in" data-aos-delay="400">
                                    <div className="text-3xl font-black leading-none mb-1">15+</div>
                                    <div className="text-[10px] uppercase font-bold tracking-widest opacity-80">Năm kinh nghiệm</div>
                                </div>
                            </div>
                        </div>
                        <div className="w-full lg:w-1/2" data-aos="fade-left">
                            <span className="text-red-600 font-bold uppercase tracking-[0.4em] text-xs mb-6 block">Our Story</span>
                            <h3 className="text-3xl md:text-5xl font-black text-slate-900 uppercase leading-tight mb-8">
                                Tận tâm kiến tạo <br /> <span className="text-red-600">giá trị vững bền</span>
                            </h3>
                            <div className="text-slate-600 text-[16px] leading-relaxed space-y-6 text-justify">
                                <p>
                                    <strong className="text-slate-900 font-black">Trung Anh Garage</strong> chuyên cung cấp dịch vụ bảo trì, bảo dưỡng và sửa chữa xe ô tô tại Hà Nội. Là một địa chỉ cung cấp dịch vụ bảo trì, bảo dưỡng và sửa chữa xe ô tô uy tín, chất lượng, chuyên nghiệp, tận tình, nhanh chóng, giá tốt nhất.
                                </p>
                                <p>
                                    Với đội ngũ nhân viên chuyên nghiệp kỹ thuật cao có tinh thần trách nhiệm với nghề nghiệp của mình được đào tạo về dịch vụ sửa chữa bảo trì thay thế phụ tùng ô tô một cách bài bản. Cùng với đó là những trang thiết bị máy móc cho sửa chữa hiện đại, được đầu tư mới và cải tiến thường xuyên nhằm phục vụ đầy đủ, toàn diện nhất cho quý khách hàng.
                                </p>
                            </div>
                            <div className="mt-12 flex flex-wrap gap-6 items-center">
                                <Link to="/gioi-thieu/ve-chung-toi" className="inline-flex items-center gap-3 bg-slate-900 hover:bg-black text-white hover:text-white font-bold py-4 px-10 rounded-2xl transition-all duration-300 transform hover:-translate-y-1 shadow-xl">
                                    Tìm hiểu thêm <ChevronRight size={18} className="text-red-600" />
                                </Link>
                                <div className="flex -space-x-4">
                                    {[1, 2, 3, 4].map(i => (
                                        <div key={i} className="w-12 h-12 rounded-full border-4 border-slate-900 bg-slate-500 overflow-hidden shadow-lg relative group/avatar">
                                            <img src={`/assets/image${i}.png`} alt="User" className="w-full h-full object-cover transition-transform group-hover/avatar:scale-125" />
                                            <div className="absolute inset-0 bg-red-600/20 opacity-0 group-hover/avatar:opacity-100 transition-opacity" />
                                        </div>
                                    ))}
                                    <div className="w-12 h-12 rounded-full border-4 border-slate-900 bg-red-600 flex items-center justify-center text-white text-[10px] font-black shadow-lg animate-pulse">
                                        +1k
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ════ TIN TỨC & SỰ KIỆN — SCIENTIFIC REDESIGN ════ */}
            <section className="py-16 lg:py-32 bg-tech-grid relative overflow-hidden">
                <div className="container max-w-7xl mx-auto px-4">
                    <div className="flex flex-col md:flex-row md:items-end justify-between mb-20 gap-8" data-aos="fade-up">
                        <div className="flex flex-col relative">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-2 h-2 bg-red-600 rounded-full" />
                                <span className="text-red-600 font-mono-tech uppercase tracking-[0.3em] text-[10px]">System Logs / News</span>
                            </div>
                            <h3 className="text-4xl md:text-6xl font-black text-slate-900 uppercase leading-none tracking-tighter">
                                Tin tức <span className="text-red-600">&</span> sự kiện
                            </h3>
                        </div>
                        <Link to="/blog" className="group flex items-center gap-4 bg-slate-900 text-white hover:text-white px-8 py-4 rounded-full font-bold uppercase tracking-widest text-[10px] hover:bg-primary transition-all duration-500 shadow-xl">
                            Xem tất cả <ArrowRight size={14} className="transition-transform group-hover:translate-x-2" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                        {loading && homeNews.length === 0 ? (
                            Array(3).fill(0).map((_, i) => (
                                <div key={i} className="bg-slate-200 border border-slate-100 rounded-3xl aspect-[3/4] animate-pulse shadow-sm" />
                            ))
                        ) : homeNews.slice(0, 3).map((news, idx) => (
                            <div
                                key={news.id || idx}
                                className="group flex flex-col bg-white border-tech p-0 transition-all duration-500 hover:shadow-[20px_20px_0px_rgba(255,0,0,0.05)] relative"
                                data-aos="fade-up"
                                data-aos-delay={idx * 150}
                            >
                                {/* Technical Corner Accents */}
                                <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-slate-100 pointer-events-none" />
                                <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-slate-100 pointer-events-none" />

                                <div className="relative aspect-[16/10] overflow-hidden m-6 mb-0">
                                    <img
                                        src={normalizeImageUrl(news.imageUrl)}
                                        alt={news.title}
                                        className="w-full h-full object-cover transition-all duration-1000 grayscale group-hover:grayscale-0 group-hover:scale-105"
                                    />
                                    {/* Scanning Line Effect */}
                                    <div className="absolute inset-x-0 h-[2px] bg-red-600/50 shadow-[0_0_15px_rgba(255,0,0,0.5)] z-20 animate-scan pointer-events-none opacity-0 group-hover:opacity-100" />

                                    <div className="absolute bottom-4 left-4 flex gap-2">
                                        <span className="bg-slate-900 text-white font-mono-tech text-[9px] px-3 py-1.5 uppercase tracking-widest border-l-2 border-red-600">
                                            {news.category || 'Article'}
                                        </span>
                                        <span className="bg-white/90 backdrop-blur-sm text-slate-900 font-mono-tech text-[9px] px-3 py-1.5 uppercase tracking-widest border border-slate-200 hidden md:block">
                                            SECURE_LINK
                                        </span>
                                    </div>
                                </div>

                                <div className="p-8 flex flex-col flex-grow">
                                    <div className="flex items-center justify-between mb-6 text-[10px] font-mono-tech font-bold text-slate-400">
                                        <div className="flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 bg-red-600 rounded-full" />
                                            <span>{new Date(news.createdAt).toLocaleDateString('vi-VN')}</span>
                                        </div>
                                    </div>

                                    <h4 className="text-[20px] font-black text-slate-900 mb-8 group-hover:text-red-600 transition-colors line-clamp-2 leading-tight uppercase tracking-tight">
                                        <Link to={`/blog/${news.slug}`}>{news.title}</Link>
                                    </h4>

                                    <div className="mt-auto pt-8 border-t border-slate-100 flex items-center justify-between">
                                        <Link
                                            to={`/blog/${news.slug}`}
                                            className="font-mono-tech text-red-600 font-black text-[11px] uppercase tracking-[0.25em] flex items-center gap-3 group/link hover:text-slate-900 transition-colors"
                                        >
                                            <span className="opacity-50">{">>"}</span> EXECUTE_READ
                                            <div className="w-5 h-[1.5px] bg-red-600 transition-all group-hover/link:w-12 group-hover/link:bg-slate-900" />
                                        </Link>
                                        <div className="flex flex-col items-end">
                                            <div className="text-[8px] font-mono-tech text-slate-400 uppercase tracking-tighter">Transmission: Stable</div>
                                            <div className="text-[8px] font-mono-tech text-red-600/50 uppercase tracking-tighter">v.1.0.4-rc1</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ════ TẠI SAO NÊN CHỌN CHÚNG TÔI ════ */}
            <section className="relative py-16 lg:py-32 bg-cover bg-center bg-no-repeat bg-fixed" style={{ backgroundImage: 'url(/assets/introduce.png)' }}>
                {/* Modern Overlays for depth - lightened slightly from original */}
                <div className="absolute inset-0 bg-[#0a0a0a]/75 mix-blend-multiply" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/60" />

                <div className="container relative max-w-7xl mx-auto px-4 z-10">
                    <div className="flex flex-col items-center mb-20" data-aos="fade-down">
                        <span className="text-red-600 font-bold uppercase tracking-[0.4em] text-[10px] mb-4">Core Values</span>
                        <h3 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tighter text-center">
                            Tại sao nên chọn <span className="text-red-600 font-black">Trung Anh Garage?</span>
                        </h3>
                        <div className="w-20 h-1.5 bg-red-600 mt-8 rounded-full" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
                        {[
                            { icon: "/assets/image3.png", title: 'Chất lượng', desc: 'Trung Anh Garage luôn luôn cố gắng để trở thành nơi cung cấp hàng đầu các dịch vụ Sửa chữa, bảo dưỡng các dòng xe hơi tại Hà Nội.' },
                            { icon: "/assets/image2.png", title: 'Uy tín', desc: 'Chúng tôi cam kết sử dụng những sản phẩm, phụ tùng chính hãng, đảm bảo lợi ích của khách hàng được tốt nhất.' },
                            { icon: "/assets/image4.png", title: 'Kinh nghiệm', desc: 'Đội ngũ Kỹ sư KTV với hơn 15 năm kinh nghiệm tự hào luôn tiên phong trong việc cung ứng dịch vụ cho các loại xe từ thông dụng đến cao cấp.' },
                            { icon: "/assets/image1.png", title: 'Giải pháp đồng bộ', desc: 'Áp dụng những công nghệ tiên tiến nhất trong việc chẩn đoán lỗi, quy trình hiện đại với 7 bước chuyên nghiệp phục vụ khách hàng.' }
                        ].map((item, idx) => (
                            <div key={idx} className="flex flex-col items-center text-center group" data-aos="fade-up" data-aos-delay={idx * 150}>
                                <div className="mb-10 relative">
                                    <div className="absolute inset-0 bg-red-600/20 blur-[30px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    <img
                                        src={item.icon}
                                        alt={item.title}
                                        className="w-[120px] h-[120px] object-contain relative z-10 transition-all duration-700 group-hover:scale-110 group-hover:drop-shadow-[0_0_20px_rgba(255,0,0,0.4)]"
                                    />
                                </div>
                                <h4 className="text-lg font-black text-white uppercase mb-6 tracking-widest group-hover:text-red-500 transition-colors duration-500">{item.title}</h4>
                                <p className="text-slate-400 text-[14px] leading-relaxed px-4 font-medium transition-colors group-hover:text-slate-200">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>


            {/* ── REVIEWS ── */}
            <section className="pt-12 lg:pt-24 pb-0 bg-slate-50 overflow-hidden">
                <div className="container max-w-7xl mx-auto px-4">
                    <div className="flex flex-col items-center mb-16" data-aos="fade-down">
                        <span className="text-red-600 font-bold uppercase tracking-[0.4em] text-[10px] mb-4">Customer Voice</span>
                        <h3 className="text-3xl md:text-4xl font-black text-slate-900 uppercase tracking-tighter text-center">
                            Khách hàng <span className="text-red-600">tiêu biểu</span>
                        </h3>
                        <div className="w-16 h-1 bg-red-600 mt-6 rounded-full" />
                    </div>

                    <div className="slide-review">
                        <Swiper
                            modules={[Navigation, Autoplay, Pagination]}
                            spaceBetween={32}
                            slidesPerView={1}
                            breakpoints={{
                                768: { slidesPerView: 2 },
                                1024: { slidesPerView: 3 },
                            }}
                            pagination={{ clickable: true }}
                            autoplay={{ delay: 5000, disableOnInteraction: false }}
                            loop
                            className="!pb-12"
                        >
                            {loading && customers.length === 0 ? (
                                Array(3).fill(0).map((_, i) => (
                                    <SwiperSlide key={i}>
                                        <div className="bg-slate-200 p-10 rounded-[2.5rem] shadow-sm animate-pulse h-[350px] border border-slate-100" />
                                    </SwiperSlide>
                                ))
                            ) : customers.map((rev, i) => (
                                <SwiperSlide key={rev._id || rev.id || i}>
                                    <div className="group h-full flex flex-col bg-white p-8 rounded-[2.5rem] shadow-[0_15px_50px_rgba(0,0,0,0.03)] border border-slate-100 hover:shadow-[0_30px_70px_rgba(255,0,0,0.1)] transition-all duration-700 hover:-translate-y-3 relative overflow-hidden backdrop-blur-sm">
                                        {/* Background Accent */}
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-red-600/[0.03] rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl group-hover:bg-red-600/[0.08] transition-all duration-700" />

                                        <div className="relative aspect-[16/10] rounded-[1.8rem] overflow-hidden mb-8 shadow-xl group-hover:shadow-red-600/10 transition-all duration-1000">
                                            <img
                                                src={normalizeImageUrl(rev.imageUrl || rev.urlImg || rev.img)}
                                                alt={rev.name}
                                                loading="lazy"
                                                className="w-full h-full object-cover transition-all duration-1000 group-hover:scale-105"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                                        </div>

                                        <div className="relative flex-grow px-2">
                                            {/* Modern Quote Icon */}
                                            <div className="text-red-600/10 text-[6rem] font-serif absolute -top-12 -left-4 leading-none select-none pointer-events-none group-hover:text-red-600/20 transition-colors">“</div>
                                            <div className="relative z-10 text-slate-500 text-[14.5px] leading-relaxed italic mb-8 font-medium">
                                                {rev.content || rev.description || rev.desc}
                                            </div>
                                        </div>

                                        <div className="flex flex-col items-center border-t border-slate-50 pt-8 mt-auto">
                                            <h3 className="font-black text-slate-900 text-[13px] uppercase tracking-[0.25em] mb-3 group-hover:text-red-600 transition-colors text-center">
                                                {rev.name}
                                            </h3>
                                            <div className="flex items-center gap-3">
                                                <div className="flex gap-1">
                                                    {[1, 2, 3, 4, 5].map(star => (
                                                        <div key={star} className="w-2 h-2 bg-red-600 rounded-full shadow-[0_0_10px_rgba(255,0,0,0.3)]" />
                                                    ))}
                                                </div>
                                                <div className="h-3 w-[1px] bg-slate-200" />
                                                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Đã xác thực</span>
                                            </div>
                                        </div>

                                        {/* Bottom Accent Line */}
                                        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-600/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default HomePage;



